import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { QuickSearch } from '../Common/QuickSearch';
import styles from './Layout.module.css';

interface LayoutProps {
  children: React.ReactNode;
  showSidebar?: boolean;
}

export const Layout: React.FC<LayoutProps> = ({ children, showSidebar = true }) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [awaitingSecondKey, setAwaitingSecondKey] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isEditing = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
      
      // Ctrl+O or Cmd+O for quick search
      if ((e.ctrlKey || e.metaKey) && (e.key === 'o' || e.key === 'O')) {
        e.preventDefault();
        e.stopPropagation();
        setSearchOpen(true);
        return;
      }
      
      // Ctrl+K or Cmd+K to enter command mode
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        e.stopPropagation();
        setAwaitingSecondKey(true);
        // Reset after 2 seconds if no second key is pressed
        setTimeout(() => setAwaitingSecondKey(false), 2000);
        return;
      }
      
      // If we're awaiting a second key after Ctrl+K
      if (awaitingSecondKey) {
        e.preventDefault();
        e.stopPropagation();
        setAwaitingSecondKey(false);
        
        // N for New Document
        if (e.key === 'n' || e.key === 'N') {
          navigate('/new');
        }
        
        // S for Save (emit custom event that editor can listen to)
        if (e.key === 's' || e.key === 'S') {
          const saveEvent = new CustomEvent('editorSave');
          document.dispatchEvent(saveEvent);
        }
        return;
      }
      
      // For other shortcuts, don't trigger when editing
      if (isEditing) {
        return;
      }
      
      // N alone for New Document (when not awaiting second key)
      if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        e.stopPropagation();
        navigate('/new');
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown, { capture: true });
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [navigate, awaitingSecondKey]);

  return (
    <div className={styles.layout}>
      <Header />
      <div className={styles.main}>
        {showSidebar && (
          <>
            <button 
              className={styles.mobileMenuButton}
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Toggle navigation"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>
            <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
          </>
        )}
        <main className={styles.content}>{children}</main>
      </div>
      <QuickSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      {awaitingSecondKey && (
        <div className={styles.commandModeIndicator}>
          <div className={styles.commandModeContent}>
            <span className={styles.commandModeTitle}>Command Mode</span>
            <div className={styles.commandModeHint}>
              Press <kbd>N</kbd> for New Document<br/>
              Press <kbd>S</kbd> to Save (when editing)
            </div>
          </div>
        </div>
      )}
      <div style={{ position: 'fixed', bottom: '16px', right: '16px', fontSize: '12px', color: 'var(--text-secondary)', opacity: 0.6, pointerEvents: 'none' }}>
        <kbd>N</kbd> New • <kbd>E</kbd> Edit (on doc pages)
      </div>
    </div>
  );
};
